<template>
  <div
    class="flex flex-col items-start gap-y-3 overflow-y-auto rounded-b-md px-2 pt-6 pb-2"
  >
    <button
      v-if="canClose"
      class="absolute z-30 top-0 right-0 bg-gray-300 w-5 h-5 flex items-center justify-center rounded-full"
      @click.stop="$emit('close-dialog')"
    >
      <icon-base
        :icon="IconClose"
        width="12"
        height="12"
        view-box="0 0 18 18"
      />
    </button>
    <div class="email-header-row to">
      <div class="left">{{ $t("COMMON.TO") }}:</div>
      <div class="right">
        <div class="relative flex w-full gap-x-2">
          <icon-base
            v-if="toInputWarning.show"
            v-tooltip="toInputWarning.text"
            :icon="IconWarning"
            data-cy="to-warning"
            class="field-warning"
          />
          <multiselect-dropdown
            class="multiselect-dropdown w-full"
            :class="{ empty: !selectedRecipients.length }"
            data-cy="email-template-to"
            name="to"
            placeholder=""
            :value="selectedRecipients"
            :options="recipientOptions"
            :can-format-option="false"
            is-absolute-positioned
            scrollable-parent="do-service"
            :allow-trim="false"
            is-rounded
            @change="handleUpdateRecipients"
          />
          <field-error-message
            v-if="erroredFields.includes('to')"
            :value="$t('COMMON.VALUE_IS_REQUIRED')"
          />
          <div
            v-if="!showCc"
            data-cy="show-cc-field"
            class="cursor-pointer font-medium text-primary hover:underline"
            @click="showCc = true"
          >
            {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.CC") }}
          </div>
          <div
            v-if="!showBcc && !showCc"
            data-cy="show-bcc-field"
            class="cursor-pointer font-medium text-primary hover:underline"
            @click="showBcc = true"
          >
            {{ $t("EMAIL_INTEGRATION.BCC") }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="showCc" class="email-header-row cc">
      <div class="left">
        {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.CC") }}:
      </div>
      <div v-if="!loadingTemplate" class="right relative">
        <div class="flex w-full">
          <icon-base
            v-if="ccInputWarning.show"
            v-tooltip="ccInputWarning.text"
            :icon="IconWarning"
            data-cy="cc-warning"
            class="field-warning"
          />
          <lf-email-input-multiple
            :value="ccInput"
            :emails="ccEmails"
            class="sending-email-multiple-input"
            @change="handleCcChange"
          />
          <div
            v-if="!showBcc"
            data-cy="show-bcc-from-cc"
            class="cursor-pointer font-medium text-primary hover:underline"
            @click="showBcc = true"
          >
            {{ $t("EMAIL_INTEGRATION.BCC") }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="showBcc" class="email-header-row bcc">
      <div class="left">{{ $t("EMAIL_INTEGRATION.BCC") }}:</div>
      <div v-if="!loadingTemplate" class="right relative">
        <icon-base
          v-if="bccInputWarning.show"
          v-tooltip="bccInputWarning.text"
          :icon="IconWarning"
          data-cy="bcc-warning"
          class="field-warning"
        />
        <lf-email-input-multiple
          :value="bccInput"
          :emails="bccEmails"
          class="sending-email-multiple-input"
          @change="handleBccChange"
        />
      </div>
    </div>
    <div class="email-header-row from">
      <div class="left">{{ $t("COMMON.FROM") }}:</div>
      <div class="right">
        <div class="w-full">
          <lf-dropdown
            v-on-click-outside="() => (showSendersDropdown = false)"
            :value="isSendAsBulk ? sender?.type : sender?.value"
            :options="senders"
            class="from-dropdown"
            :class="{ 'from-dropdown-single': !isSendAsBulk }"
            data-cy="email-template-from"
            name="from"
            :capitalize="false"
            :toggle-from-parent="showSendersDropdown"
            full-width
          >
            <template #toggle-button="{ value }">
              <button
                class="dropdown-button relative flex min-h-4 w-full cursor-pointer items-center rounded p-0 text-left text-sm focus:outline-none"
                @click.prevent="showSendersDropdown = !showSendersDropdown"
              >
                <lf-badge
                  is-rounded
                  no-margin
                  class="h-[22px] space-x-2 text-xxs leading-[18px] font-semibold tracking-[0.3px] uppercase"
                >
                  <span class="text-headline">
                    {{ getFromValue(value as string) }}
                  </span>
                  <span v-if="sender?.type && !isSendAsBulk">
                    ({{ SENDER_TYPE_MAP[sender.type] }})
                  </span>
                </lf-badge>
              </button>
            </template>
            <template #listItem="{ option, optionKey }">
              <li
                :data-cy="option"
                class="dropdown-option flex w-full cursor-pointer items-center space-x-2 p-2 hover:bg-primary-background-active"
                role="option"
                @click.stop="handleUpdateSender(optionKey as string)"
              >
                <icon-base
                  v-bind="getEmailOptionIcon(optionKey as string)"
                  class="ml-1 flex-shrink-0"
                />
                <div class="flex flex-col">
                  <span v-tooltipable class="max-w-80 truncate">
                    {{ option }}
                  </span>
                  <span
                    v-if="sender?.type && !isSendAsBulk"
                    class="w-fit-content rounded bg-disabled p-1 text-xxs font-semibold text-label uppercase"
                  >
                    {{ getSenderTypeForIndex(optionKey) }}
                  </span>
                </div>
                <icon-base
                  v-if="isSelectedFromOption(optionKey as string)"
                  :icon="IconCheck"
                  class="ml-auto text-primary"
                />
              </li>
            </template>
          </lf-dropdown>
          <field-error-message
            v-if="erroredFields.includes('from')"
            :value="$t('COMMON.VALUE_IS_REQUIRED')"
          />
        </div>
      </div>
    </div>
    <div class="email-header-row subject">
      <div class="left">
        {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.SUBJECT") }}:
      </div>
      <div class="right">
        <div class="w-full">
          <dynamic-field
            :model-value="selectedTemplate?.subject || ''"
            name="subject"
            data-cy="email-template-subject"
            class="email-headless subject-input w-full"
            :placeholder="$t('EMAIL_INTEGRATION.EMAIL_SUBJECT')"
            @update:model-value="selectedTemplate.subject = $event"
          />
          <field-error-message
            v-if="erroredFields.includes('subject')"
            :value="$t('COMMON.VALUE_IS_REQUIRED')"
          />
        </div>
      </div>
    </div>
    <div class="email-header-row template">
      <div class="left">{{ $t("COMMON.TEMPLATE") }}:</div>
      <div class="right">
        <tooltip
          ref="templatesTooltip"
          class="max-w-full"
          placement="bottom-start"
          trigger="click"
          hide-on-click="toggle"
          :on-click-outside="hideDropdown"
          :append-to="body"
          :arrow="false"
          sticky="reference"
          theme="shadow"
          max-width="none"
          interactive
          :popper-options="popperOptions"
        >
          <template #default>
            <lf-pill
              size="sm"
              class="template-pill group h-[22px] max-w-full cursor-pointer"
              color-index="35"
              opaque
            >
              <div class="flex items-center gap-x-2">
                <div data-cy="template-name" class="truncate text-left">
                  <span class="text-xs font-normal">
                    {{ selectedTemplate?.name || "" }}
                  </span>
                </div>
                <icon-base
                  :icon="IconArrowUp"
                  height="10"
                  width="10"
                  class="text-flush-orange group-hover:visible"
                  :class="
                    !templatesTooltip?.state.isVisible
                      ? 'invisible -translate-y-1 rotate-180'
                      : 'invisible'
                  "
                />
              </div>
            </lf-pill>
          </template>
          <template #content>
            <div class="w-100 rounded-md bg-white p-2 shadow-lg">
              <div class="sticky pb-2">
                <search-input
                  v-model="search"
                  data-cy="email-template-search"
                  class="h-10 w-full"
                  no-padding
                  no-margin
                />
              </div>
              <ul
                data-cy="email-templates"
                class="grid h-auto max-h-100 grid-cols-1 gap-y-2 overflow-x-hidden overflow-y-auto px-1 text-sm text-headline"
              >
                <li
                  v-for="(template, index) in filteredTemplatesOptions"
                  :key="template.id"
                  ref="templateListRef"
                  v-tooltip="{
                    content: template.name,
                    onShow: () => isTemplateOptionTruncated[index],
                  }"
                  class="h-8 cursor-pointer truncate rounded p-2 text-xs whitespace-nowrap hover:bg-grey-hover"
                  :class="{
                    'bg-primary-background-active':
                      selectedTemplate.id === template.id,
                  }"
                  @click="selectTemplate(template)"
                >
                  {{ template.name }}
                </li>
              </ul>
            </div>
          </template>
        </tooltip>
      </div>
    </div>
    <div class="email-header-row branding">
      <div class="left">
        {{ $t("COMMON.CHOOSE") }} {{ $t("COMMON.BRANDING") }}:
      </div>
      <div class="right">
        <tooltip
          ref="brandingTooltip"
          class="max-w-full"
          placement="bottom-start"
          trigger="click"
          hide-on-click="toggle"
          :on-click-outside="hideBrandingDropdown"
          :append-to="body"
          :arrow="false"
          sticky="reference"
          theme="none"
          max-width="none"
          interactive
          :popper-options="popperOptions"
        >
          <template #default>
            <lf-pill
              size="sm"
              class="template-pill group h-[22px] max-w-full cursor-pointer"
              color-index="31"
              opaque
            >
              <div class="flex items-center gap-x-2">
                <div data-cy="branding-name" class="truncate text-left">
                  <span class="text-xs font-normal">
                    {{ selectedBranding?.template_name || "" }}
                  </span>
                </div>
                <icon-base
                  :icon="IconArrowUp"
                  height="10"
                  width="10"
                  class="text-icon-user-purple group-hover:visible"
                  :class="
                    !brandingTooltip?.state.isVisible
                      ? 'invisible -translate-y-1 rotate-180'
                      : 'invisible'
                  "
                />
              </div>
            </lf-pill>
          </template>
          <template #content>
            <div class="w-100 rounded-md bg-white p-2 shadow-lg">
              <div class="sticky pb-2">
                <search-input
                  v-model="brandingSearch"
                  data-cy="branding-search"
                  class="h-10 w-full"
                  no-padding
                  no-margin
                />
              </div>
              <ul
                data-cy="branding-templates"
                class="grid h-auto max-h-100 grid-cols-1 gap-y-2 overflow-x-hidden overflow-y-auto px-1 text-sm text-headline"
              >
                <li
                  v-for="(branding, index) in filteredBrandingOptions"
                  :key="branding.id"
                  ref="brandingListRef"
                  v-tooltip="{
                    content: branding.template_name,
                    onShow: () => isBrandingOptionTruncated[index],
                  }"
                  class="h-8 cursor-pointer truncate rounded p-2 text-xs whitespace-nowrap hover:bg-grey-hover"
                  :class="{
                    'bg-primary-background-active':
                      selectedBranding?.id === branding.id,
                  }"
                  @click="selectBranding(branding)"
                >
                  {{ branding.template_name }}
                </li>
              </ul>
            </div>
          </template>
        </tooltip>
      </div>
    </div>
    <div class="relative grow self-stretch">
      <dynamic-field
        :model-value="selectedTemplate?.body || ''"
        data-cy="email-template-body"
        class="dynamic-field__body"
        :class="{ 'flex flex-col': !!replyToMessage }"
        name="body"
        show-toolbar
        toolbar-type="dynamic"
        allow-shortcuts
        @update:model-value="selectedTemplate.body = $event"
        @submit="sendEmail"
      />
      <div class="absolute right-0 bottom-[60px] left-0">
        <incoming-message
          v-if="replyToMessage"
          ref="replyToMessageRef"
          :content="replyToMessage"
          @button:click="calculateReplyToMessageBlockHeight"
        />
        <attachment-list
          v-if="!!files.length"
          :files
          @remove-file="removeFile"
        />
      </div>
      <div
        ref="sendButtonBlockRef"
        class="absolute right-0 bottom-0 left-0 rounded-br rounded-bl border-r border-b border-l bg-disabled p-2"
      >
        <field-error-message
          v-if="erroredFields.includes('body')"
          class="-ml-2"
          :value="$t('COMMON.VALUE_IS_REQUIRED')"
        />
        <div class="flex items-center justify-between">
          <div>
            {{ $t("COMMON.TYPE") }}
            <span class="bg-white px-2 py-1">[</span>
            {{
              $t(
                "ORGANIZATION.COMMUNICATION_TEMPLATES.DYNAMIC_FIELD_SYMBOL_EXPLANATION_SHORT"
              ).toLowerCase()
            }}
          </div>
          <div class="flex items-center justify-end space-x-2">
            <email-attachment @attach-file="handleAttachFile" />
            <primary-button
              id="email-send-button"
              data-cy="send-button"
              class="send-button"
              @click="sendEmail"
            >
              <icon-base :icon="IconSend" class="text-white" />
            </primary-button>
          </div>
        </div>
      </div>
    </div>
    <primary-button
      id="email-send-button"
      data-cy="send-button-2"
      class="send-button"
      :class="targetIsVisible ? 'hidden' : 'sticky bottom-0 left-full'"
      @click="sendEmail"
    >
      <icon-base :icon="IconSend" class="text-white" />
    </primary-button>
    <loader :is-loading="loading" />
  </div>
</template>

<script setup lang="ts">
import { useElementVisibility } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, toRaw, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import cloneDeep from "lodash/cloneDeep";
import { storeToRefs } from "pinia";

import { useAnalyticsRouteSource } from "@/composables/useAnalyticsRouteSource";
import { AnalyticsEvent } from "@/enums/analytics";
import { EmailAliasType, EmailBrandingId } from "@/enums/emailCustomization";
import { TextEditorShortcut } from "@/enums/keyboardShortcuts";
import { defaultEmailTypes } from "@/helpers/constants/emails";
import { OPEN_TEMPLATES } from "@/helpers/constants/events";
import { calculateEmailBottomPadding } from "@/helpers/email";
import { useEmitter } from "@/hooks/common";
import { usePromiseWrapper } from "@/hooks/common";
import { useElementStatus } from "@/hooks/elements";
import { useInbox } from "@/hooks/inbox";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { useNotification } from "@/hooks/notifications";
import { useSendEmailMutation } from "@/hooks/queries/useInbox";
import { type EmailTemplate } from "@/models/clients";
import { useAnalyticsStore } from "@/stores/analytics";
import useEmailsStore from "@/stores/emails";

import DynamicField from "@/components/DynamicField.vue";
import AttachmentList from "@/components/emailSending/AttachmentList.vue";
import EmailAttachment from "@/components/emailSending/EmailAttachment.vue";
import IncomingMessage from "@/components/emailSending/IncomingMessage.vue";
import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import IconCheck from "@/components/icons/IconCheck.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconGoogleColored from "@/components/icons/IconGoogleColored.vue";
import IconLendflow from "@/components/icons/IconLendflow.vue";
import IconSend from "@/components/icons/IconSend.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfBadge from "@/components/ui/LfBadge.vue";
import FieldErrorMessage from "@/components/ui/inputs/FieldErrorMessage.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfEmailInputMultiple from "@/components/ui/inputs/LfEmailInputMultiple.vue";
import MultiselectDropdown from "@/components/ui/inputs/MultiselectDropdown.vue";

import type { IEmailCustomization } from "@/models/clients";
import type { EmailAddress } from "@/models/emails";
import type { MultiSelectOption } from "@/models/options";
import type { TippyComponent } from "vue-tippy";

const {
  replyToMessage = "",
  canClose = false,
  selectedEmail = "",
} = defineProps<{
  replyToMessage?: string;
  canClose?: boolean;
  selectedEmail?: string;
}>();

const VALUE_TYPE_SEPARATOR = "|" as const;

const emit = defineEmits(["close-dialog"]);

const store = useEmailsStore();
const { analyticsRouteSource } = useAnalyticsRouteSource();
const { t } = useI18n();
const { showMessage } = useNotification();
const { trackEvent } = useAnalyticsStore();
const { isTextTruncated } = useElementStatus();
const { inboxAllActivityActive } = useInbox();
const emitter = useEmitter();
const { assignShortcutToAction } = useKeyboardShortcuts();

type SendEmailTemplate = Pick<
  EmailTemplate,
  "name" | "subject" | "body" | "cc" | "bcc"
> & {
  id?: string;
};
export interface EmailFormPayload {
  applications: Record<string, string[]>;
  body: string;
  preferred_email_address: EmailAddress & {
    send_as_bulk: number;
  };
  subject: string;
  bcc?: string[];
  cc?: string[];
  email_customization_id?: number;
  email_customization_use_default?: number;
  email_template_id?: string;
  files?: File[];
}

const SENDER_TYPE_MAP = {
  [EmailAliasType.Alias]: t("EMAIL_INTEGRATION.ALIAS"),
  [EmailAliasType.Primary]: t("EMAIL_INTEGRATION.USER_ACCOUNT_EMAIL"),
  [EmailAliasType.Whitelabel]: t("EMAIL_INTEGRATION.SYSTEM_EMAIL"),
  [EmailAliasType.GmailAlias]: t("EMAIL_INTEGRATION.GMAIL_ALIAS"),
  [EmailAliasType.GmailPrimary]: t("EMAIL_INTEGRATION.GMAIL_PRIMARY"),
};

const templateListRef = ref<(HTMLElement | null)[]>([]);
const brandingListRef = ref<(HTMLElement | null)[]>([]);
const replyToMessageRef = useTemplateRef("replyToMessageRef");
const showSendersDropdown = ref(false);
const showCc = ref(false);
const showBcc = ref(false);
const sendButtonBlockRef = ref<HTMLElement | null>(null);
const search = ref("");
const brandingSearch = ref("");
const loadingTemplate = ref(false);
const templatesTooltip = ref<TippyComponent | null>(null);
const brandingTooltip = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["top-start"] },
    },
  ],
});
const body = ref(document.body);
const targetIsVisible = useElementVisibility(sendButtonBlockRef);

const { modalData, recipientOptions, suppressedEmails } = storeToRefs(store);

modalData.value.templates.unshift({
  id: "0",
  name: t("COMMON.SELECT"),
  subject: "",
  body: "",
});
const selectedTemplate = ref<SendEmailTemplate>(
  cloneDeep(modalData.value?.templates?.[0]) || {
    name: "",
    subject: "",
    body: "",
    cc: [],
    bcc: [],
  }
);
const senders = computed(
  () =>
    modalData.value?.email_addresses?.reduce(
      (res: Record<string, string>, { value, type }) => {
        const itemValue = isSendAsBulk.value ? type : value;
        const key = `${value}${VALUE_TYPE_SEPARATOR}${type}`;

        if (!isSendAsBulk.value) {
          res[key] = itemValue;
          return res;
        }

        const name = defaultEmailTypes.find(
          (item) => item.id === itemValue
        )?.name;

        const savedValues = Object.values(res);

        /**
         * For bulk sending we use just types, so if a type
         * has already been saved as an option, skip it
         */

        if (!name || savedValues.includes(name)) {
          return res;
        }

        res[key] = name;
        return res;
      },
      {}
    ) || {}
);

const toggleTemplates = (focused: boolean = false) => {
  if (!templatesTooltip.value) {
    return;
  }
  if (focused) {
    // check for focus in textarea
    // if we don't have focus in textarea, return early to avoid double toggle
    if (document.activeElement?.tagName !== "TEXTAREA") {
      return;
    }
  }
  templatesTooltip.value?.state.isVisible
    ? templatesTooltip.value?.hide()
    : templatesTooltip.value?.show();
};

emitter.on(OPEN_TEMPLATES, () => toggleTemplates(true));

const isSelectedFromOption = (option: string) => {
  /**
   * option is of type value + separator + type
   */

  const [value, type] = option.split(VALUE_TYPE_SEPARATOR);
  if (isSendAsBulk.value) {
    return sender.value?.type === type;
  }
  return sender.value?.value === value && sender.value?.type === type;
};

const bottomPadding = computed(() => {
  const attachmentsPadding = calculateEmailBottomPadding(files.value.length);
  return `${parseInt(attachmentsPadding, 10) + (replyToMessageBlockHeight.value || 0)}px`;
});

const getFromValue = (value: string) => {
  if (!value) {
    return t("COMMON.FROM");
  }

  if (isSendAsBulk.value) {
    return defaultEmailTypes.find((item) => item.id === value)?.name ?? "-";
  }

  return value;
};

const sender = ref<EmailAddress | null>(
  modalData.value?.email_addresses?.[0] ?? null
);

const isSendAsBulk = computed(
  () => !!(modalData.value?.applications?.length > 1)
);

const getInitialRecipients = () => {
  const options = recipientOptions.value.map(({ id }) => ({ id }));
  if (!selectedEmail) {
    return options;
  }
  const recipient = recipientOptions.value.find(
    (option) => option.name?.toLowerCase() === selectedEmail.toLowerCase()
  );
  return recipient ? [{ id: recipient.id }] : options;
};

const selectedRecipients = ref<Pick<MultiSelectOption, "id">[]>(
  getInitialRecipients()
);
const ccInput = ref<string | null>(null);
const bccInput = ref<string | null>(null);
const ccEmails = ref<string[]>([]);
const bccEmails = ref<string[]>([]);
const errorMessage = ref<string | null>(null);
const erroredFields = ref<string[]>([]);
const files = ref<File[]>([]);
const isTemplateOptionTruncated = isTextTruncated(templateListRef);
const isBrandingOptionTruncated = isTextTruncated(brandingListRef);
const commonBrandingOptions = ref([
  {
    id: EmailBrandingId.None,
    template_name: `${t("COMMON.NO")} ${t("COMMON.BRANDING")}`,
  },
  {
    id: EmailBrandingId.Default,
    template_name: `${t("COMMON.DEFAULT")} ${t("COMMON.BRANDING")}`,
  },
]);
const selectedBranding = ref<Pick<
  IEmailCustomization,
  "id" | "template_name"
> | null>(commonBrandingOptions.value[0]);

const replyToMessageBlockHeight = ref(0);

const toInputWarning = computed(() => {
  const matchedSuppressed = selectedRecipients.value.reduce<string[]>(
    (acc, recipient) => {
      const email = recipient.id.split(VALUE_TYPE_SEPARATOR)[1];
      if (email && suppressedEmails.value.has(email)) {
        acc.push(email);
      }
      return acc;
    },
    []
  );
  return {
    show: !!matchedSuppressed.length,
    text: t("ORGANIZATION.COMMUNICATION_TEMPLATES.SUPPRESSED_EMAILS_WARNING", {
      emails: matchedSuppressed.join(", "),
    }),
  };
});

const ccInputWarning = computed(() => ({
  text: t("ORGANIZATION.COMMUNICATION_TEMPLATES.EMAILS_INPUT_WARNING", {
    inputName: t("ORGANIZATION.COMMUNICATION_TEMPLATES.CC").toUpperCase(),
    emails: filterEmailsInput(ccInput.value),
  }),
  show: !!ccInput.value?.trim().length,
}));

const bccInputWarning = computed(() => ({
  text: t("ORGANIZATION.COMMUNICATION_TEMPLATES.EMAILS_INPUT_WARNING", {
    inputName: t("EMAIL_INTEGRATION.BCC").toUpperCase(),
    emails: filterEmailsInput(bccInput.value),
  }),
  show: !!bccInput.value?.trim().length,
}));

const filteredTemplatesOptions = computed(() => {
  return modalData.value?.templates.filter(
    (field) =>
      field.name?.toLowerCase().includes(search.value.toLowerCase()) &&
      field.name !== t("COMMON.SELECT")
  );
});

const filteredBrandingOptions = computed(() => {
  const filtered = (modalData.value?.customizations ?? [])
    ?.filter(
      (branding) =>
        branding.template_name
          ?.toLowerCase()
          .includes(brandingSearch.value.toLowerCase()) &&
        branding.template_name !== t("COMMON.SELECT")
    )
    .map((branding) => ({
      id: branding.id,
      template_name: branding.template_name,
    }));
  return [...commonBrandingOptions.value, ...filtered];
});

const selectTemplate = (template: SendEmailTemplate) => {
  loadingTemplate.value = true;
  selectedTemplate.value = structuredClone(toRaw(template));
  ccEmails.value = selectedTemplate.value.cc || ccEmails.value;
  bccEmails.value = selectedTemplate.value.bcc || bccEmails.value;
  if (ccEmails.value.length) {
    showCc.value = true;
  }
  if (bccEmails.value.length) {
    showBcc.value = true;
  }
  hideDropdown();
  // this is required to ensure the cc/bcc inputs reflect the values properly
  nextTick(() => (loadingTemplate.value = false));
};

const selectBranding = (
  branding: Pick<IEmailCustomization, "id" | "template_name">
) => {
  selectedBranding.value = branding;
  hideBrandingDropdown();
};

const filterEmailsInput = (inputValue: string | null) =>
  inputValue
    ?.split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .join(", ");

const handleUpdateSender = (key: string) => {
  /**
   * key is of type value + separator + type
   */
  const [selectedValue, selectedType] = key.split(VALUE_TYPE_SEPARATOR);
  sender.value =
    modalData.value?.email_addresses.find(({ value, type }) => {
      if (isSendAsBulk.value) {
        return type.toString() === selectedType;
      }
      return value === selectedValue && type === selectedType;
    }) || null;
  showSendersDropdown.value = false;
};

const handleUpdateRecipients = (value: Pick<MultiSelectOption, "id">[]) => {
  selectedRecipients.value = value;
};

const handleCcChange = ({
  validEmails,
  input,
}: {
  validEmails: string[];
  input: string;
}) => {
  ccInput.value = input;
  ccEmails.value = validEmails;
};

const handleBccChange = ({
  validEmails,
  input,
}: {
  validEmails: string[];
  input: string;
}) => {
  bccInput.value = input;
  bccEmails.value = validEmails;
};

const getSenderTypeForIndex = (optionKey: string) => {
  const [, type] = optionKey.split(VALUE_TYPE_SEPARATOR);
  const key = type.toLowerCase() as keyof typeof SENDER_TYPE_MAP;
  return SENDER_TYPE_MAP[key] || "-";
};

const getEmailOptionIcon = (valueType: string) => {
  const [, type] = valueType.split(VALUE_TYPE_SEPARATOR);
  if (type?.toLowerCase().includes("gmail")) {
    return {
      icon: IconGoogleColored,
    };
  }

  return {
    icon: IconLendflow,
    viewBox: "14 12 26 26",
  };
};

const calculateReplyToMessageBlockHeight = async () => {
  await nextTick();
  requestAnimationFrame(() => {
    const textPadding = 50;
    replyToMessageBlockHeight.value = replyToMessage
      ? (replyToMessageRef.value?.$el?.offsetHeight ?? 0) + textPadding
      : 0;
  });
};

const { mutateAsync: sendEmailMutation } = useSendEmailMutation(
  inboxAllActivityActive.value
);

const { loading, fetchWrapper: sendEmail } = usePromiseWrapper(
  async () => {
    errorMessage.value = null;
    erroredFields.value = [];
    if (
      !sender.value ||
      !selectedRecipients.value.length ||
      !selectedTemplate.value.subject ||
      !selectedTemplate.value.body
    ) {
      erroredFields.value = [
        !sender.value ? "from" : "",
        !selectedRecipients.value.length ? "to" : "",
        !selectedTemplate.value.subject ? "subject" : "",
        !selectedTemplate.value.body ? "body" : "",
      ].filter(Boolean) as string[];
      errorMessage.value = t(
        "ORGANIZATION.COMMUNICATION_TEMPLATES.MISSING_DATA"
      );
      return;
    }
    /*
     * Recipient id is: id|email
     */
    const recipients = selectedRecipients.value.map(
      (recipient) => recipient.id
    );
    const applicationsPayload = modalData.value.applications.reduce<
      Record<string, string[]>
    >((apps, currApp) => {
      if (selectedEmail) {
        /*
         * Selected email is present only inside a deal, select from deal emails
         */
        apps[currApp.id] = recipientOptions.value.reduce<string[]>(
          (selectedEmails, recipient) => {
            if (recipients.includes(recipient.id)) {
              selectedEmails.push(recipient.name);
            }
            return selectedEmails;
          },
          []
        );
        return apps;
      }
      const recipientsIds = recipients.map((recipient) => {
        const ownerId = recipient.split(VALUE_TYPE_SEPARATOR)[0];
        return ownerId ? Number(ownerId) : 0;
      });
      const applicationRecipients = currApp.owners
        .filter(({ personal_info_id }) =>
          recipientsIds.includes(personal_info_id)
        )
        .map(({ email_address }) => email_address);
      apps[currApp.id] = applicationRecipients;
      return apps;
    }, {});

    const payload: EmailFormPayload = {
      applications: applicationsPayload,
      subject: selectedTemplate.value.subject,
      body: selectedTemplate.value.body,
      preferred_email_address: {
        ...sender.value,
        send_as_bulk: isSendAsBulk.value ? 1 : 0,
      },
      cc: ccEmails.value.length ? ccEmails.value : undefined,
      bcc: bccEmails.value.length ? bccEmails.value : undefined,
    };

    if (selectedBranding.value?.id === EmailBrandingId.Default) {
      payload.email_customization_use_default = 1;
    } else if (selectedBranding.value && selectedBranding.value.id > 0) {
      payload.email_customization_id = selectedBranding.value.id;
    }

    if (selectedTemplate.value?.id && selectedTemplate.value.id !== "0") {
      payload.email_template_id = selectedTemplate.value.id;
    }

    await sendEmailMutation(
      {
        email: payload,
        files: files.value,
      },
      {
        onSuccess: () => {
          trackEvent(AnalyticsEvent.CommunicationEmailSent, {
            recipient_count: selectedRecipients.value.length,
            has_attachments: files.value.length > 0,
            template_used:
              !!selectedTemplate.value?.id && selectedTemplate.value.id !== "0",
            ...analyticsRouteSource.value,
          });
          emit("close-dialog");
        },
      }
    );
  },
  {
    finally: () => {
      if (errorMessage.value) {
        showMessage(errorMessage.value, "error");
        return;
      }
    },
  }
);

const hideDropdown = () => {
  templatesTooltip.value?.hide();
};
const hideBrandingDropdown = () => {
  brandingTooltip.value?.hide();
};

const handleAttachFile = (uploadedFiles: FileList) => {
  const newFiles = Array.from(uploadedFiles).filter(
    (file) => !files.value.some((f) => f.name === file.name)
  );
  files.value = [...files.value, ...newFiles];
};

const removeFile = (file: File) => {
  files.value = files.value.filter((f) => f.name !== file.name);
};

assignShortcutToAction(TextEditorShortcut.ChooseTemplate, toggleTemplates);

onMounted(() => {
  setTimeout(calculateReplyToMessageBlockHeight, 50);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.email-header-row {
  @apply flex w-full items-start gap-x-3;

  .left {
    @apply w-1/5 pl-4 font-semibold text-label;
  }

  .right {
    @apply relative flex w-4/5 justify-start pr-4;
  }
}

.field-warning {
  @apply absolute top-0-5 -left-5 text-orange-400;
}

:deep(.from-dropdown.from-dropdown-single) {
  .input-value {
    @apply text-primary;
  }
}

.from-dropdown :deep(.dropdown-list) {
  @apply m-2 w-[unset];
}

:deep() {
  .dynamic-field__body {
    @apply h-full;

    > .flex {
      @apply h-full;

      .ql-container {
        max-height: none;

        .ql-editor {
          max-height: none;
          padding-bottom: v-bind(bottomPadding);
        }
      }
    }
  }

  .multiselect-dropdown {
    @apply mb-0;

    .z-10 {
      @apply min-h-[22px] cursor-text border-none;

      .w-min {
        @apply m-0 cursor-pointer rounded-full text-xs font-semibold text-headline;

        .h-5.w-5 {
          @apply ml-1 h-4 w-4 bg-grey-icon pl-0;
        }
      }

      .right-0,
      .py-1 {
        @apply hidden;
      }
    }
  }

  .subject-input.email-headless.dynamic-field {
    .flex {
      @apply h-[22px];

      .ql-container {
        @apply p-0;
        min-height: unset !important;
        height: 22px !important;
        border: none !important;

        .ql-editor {
          height: 22px !important;
          @apply rounded;

          &.ql-blank::before {
            /* text-xs won't work here due to root font size difference */
            font-size: 12px;
            @apply left-0 font-semibold text-silver opacity-100;
          }
        }
      }
    }
  }

  .sending-email-multiple-input {
    @apply min-h-[22px] gap-1 border-none p-0 leading-[22px];

    .m-1 {
      @apply m-0 py-0 pr-1 text-xs font-semibold text-headline;

      .h-5.w-5 {
        @apply ml-1 h-4 w-4 bg-grey-icon pl-0;
      }
    }

    input[type="text"] {
      @apply h-[22px] rounded border-b border-transparent p-0 text-xs font-semibold;
    }
  }

  .template-pill {
    .space-x-2 {
      @apply font-normal text-headline;
    }

    svg {
      display: none !important;
    }

    &:hover {
      svg {
        display: block !important;
      }
    }
  }

  .send-button {
    @apply w-[34px] px-2 shadow-none;
  }
}
</style>
